import { Content, Link, LinkVariant, Spacer } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useMemo, useRef } from 'react'

import { useExceedsHeightWithClass } from '../../hooks/useExceedsHeightWithClass'
import { Image } from '../Image/Image'
import { ComplexHeroProps } from './ComplexHero.type'

export const ComplexHeroWithIllustration = (props: ComplexHeroProps) => {
  const {
    buttonTarget,
    buttonText,
    buttonUrl,
    content,
    featuredImage,
    featuredImageMobile,
    heading,
    backgroundFacet,
    componentSpacing,
    testId,
    analytics,
    psnAnalytics,
    abtestContainer
  } = props

  const headingRef = useRef<HTMLHeadingElement>(null)
  const isLargeHeading = !useExceedsHeightWithClass(
    headingRef,
    'Type-Display',
    102,
    heading
  )
  const atb = useMemo(() => {
    return new AnalyticsTagBuilder()
      .setDefaultName('ComplexHero')
      .setAnalyticsName(analytics)
      .setPsnAnalytics(psnAnalytics)
      .setAbTestAnalytics(abtestContainer)
  }, [abtestContainer, analytics, psnAnalytics])

  const facetColor = backgroundFacet || BackgroundColor.MIDNIGHT_BLUE

  const isContentReversed = facetColor !== BackgroundColor.WHITE

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className={'ComplexHero ComplexHero--withIllustration'}
        data-testid={testId ? `${testId}` : null}
        data-analytics-container="Hero"
      >
        <div className="ComplexHero-wrapper">
          <Image
            className="ComplexHero-facet"
            src={`${window.AMPCOMPATH}images/facets/heroes/complex-hero/desktop/${facetColor}.svg`}
            sm={`${window.AMPCOMPATH}images/facets/heroes/complex-hero/mobile/${facetColor}.svg`}
            priority={true}
            testId={testId ? `${testId}-facet` : undefined}
          />
          <div
            className="ComplexHero-panel u-flex u-flexAlignItemsCenter u-posRelative"
            data-testid={testId ? `${testId}-panel` : null}
          >
            <Content
              reversed={isContentReversed}
              className="ComplexHero-panelInner u-sizeFull u-posRelative"
              testId={testId ? `${testId}-content` : undefined}
            >
              <h1
                ref={headingRef}
                className={`u-textTight${isLargeHeading ? ' Type-Display' : ''}`}
                dangerouslySetInnerHTML={{ __html: heading || '' }}
                data-testid={testId ? `${testId}-heading` : null}
              ></h1>
              <div
                className="u-marginTop12 u-textDefault"
                dangerouslySetInnerHTML={{ __html: content || '' }}
                data-testid={testId ? `${testId}-body` : null}
              />
              {buttonText && buttonUrl && (
                <Link
                  className={`ComplexHero-button u-marginTop32 ${isContentReversed ? 'Button--reversed ' : ''}`}
                  variant={LinkVariant.SECONDARY}
                  name={buttonText}
                  url={buttonUrl}
                  target={buttonTarget}
                  testId={testId ? `${testId}-link` : undefined}
                  {...atb.dataAnalyticAttribute(analytics)}
                />
              )}
            </Content>
          </div>
          <div className="ComplexHero-sideImageWrapper">
            <Image
              className="ComplexHero-sideImage u-block"
              src={featuredImage || ''}
              sm={featuredImageMobile || featuredImage}
              priority={true}
              loading={'eager'}
              testId={testId ? `${testId}-side-image` : undefined}
            />
          </div>
        </div>
      </section>
    </Spacer>
  )
}
